import { Injectable } from "@angular/core";

import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/store/reducers";
import { PaginationRequest } from "src/app/models/common/paginationRequest";

import { GridUtils } from "../../utils/gridUtils";
import { AppSessionService } from "../app-session/app-session.service";
import { FilterOperation } from "src/app/models/custom-table/custom-table.model";
import {
  setAdvancedFilterQuery,
  setSimpleFilterQuery,
} from "../../utils/tableUtils";

const EMPTY_STRING = "";

@Injectable({
  providedIn: "root",
})
export class PartiesService {
  private BASE_URL = this.appSession.baseUrl + "/api/v1/parties";

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private appSession: AppSessionService
  ) {}

  getSalutations(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    let url = `${this.appSession.baseUrl + "/api/v1/parties"}/salutations`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 40,
        realSize: false,
      };
    }
    const sort = "";

    const pagination =
      "?offset=" +
      pageReq.offset +
      "&limit=" +
      pageReq.limit +
      "&real_size=" +
      pageReq.realSize;
    url = url.concat(pagination);

    return this.http.get(url, { headers });
  }

  // get party - generic type
  getParty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.appSession.baseUrl + "/api/v1/parties"}/${
      action.partyCode
    }`;
    return this.http.get(url, { headers });
  }

  // get customers
  getCustomersParty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.appSession.baseUrl + "/api/v1/parties"}/customers/${
      action.partyCode
    }`;
    return this.http.get(url, { headers });
  }

  // get suppliers
  getSuppliersParty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.appSession.baseUrl + "/api/v1/parties"}/suppliers/${
      action.partyCode
    }`;
    return this.http.get(url, { headers });
  }

  // get employees
  getEmployeesParty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.appSession.baseUrl + "/api/v1/parties"}/employees/${
      action.partyCode
    }`;
    return this.http.get(url, { headers });
  }

  // create party - generic type
  createParty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.party);
    const url = `${this.appSession.baseUrl + "/api/v1/parties"}`;
    return this.http.post(url, data, { headers });
  }

  // create customers
  createCustomersParty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.party);
    const url = `${this.appSession.baseUrl + "/api/v1/parties"}/customers`;
    return this.http.post(url, data, { headers });
  }

  // update customers
  updateCustomersParty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.party);
    const url = `${this.appSession.baseUrl + "/api/v1/parties"}/customers/${
      action.party.code
    }`;
    return this.http.put(url, data, { headers });
  }

  // create suppliers
  createSuppliersParty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.party);
    const url = `${this.appSession.baseUrl + "/api/v1/parties"}/suppliers`;
    return this.http.post(url, data, { headers });
  }

  // update suppliers
  updateSuppliersParty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.party);
    const url = `${this.appSession.baseUrl + "/api/v1/parties"}/suppliers/${
      action.party.code
    }`;
    return this.http.put(url, data, { headers });
  }

  // create employees
  createEmployeesParty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.party);
    const url = `${this.appSession.baseUrl + "/api/v1/parties"}/employees`;
    return this.http.post(url, data, { headers });
  }

  // update customers
  updateEmployeesParty(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.party);
    const url = `${this.appSession.baseUrl + "/api/v1/parties"}/employees/${
      action.party.code
    }`;
    return this.http.put(url, data, { headers });
  }

  // Get Party
  public getCustomerPartyList(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    return this.http.get(
      this.buildDataUrl(
        paging,
        filteringArgs,
        sortingArgs,
        headers,
        "/customers/search?"
      ),
      { headers }
    );
  }

  // Get Supplier
  public getSupplierPartyList(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    return this.http.get(
      this.buildDataUrl(
        paging,
        filteringArgs,
        sortingArgs,
        headers,
        "/suppliers/search?"
      ),
      { headers }
    );
  }

  // Get Employee
  public getEmployeePartyList(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    return this.http.get(
      this.buildDataUrl(
        paging,
        filteringArgs,
        sortingArgs,
        headers,
        "/employees/search?"
      ),
      { headers }
    );
  }

  public getCustomerContracts(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${
      this.appSession.baseUrl + "/api/v1/parties"
    }/customers/${action}/contracts`;
    return this.http.get(url, { headers });
  }

  public getCustomerEmployees(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const url = `${
      this.appSession.baseUrl + "/api/v1/parties"
    }/customers/${action}/employees`;
    return this.http.get(url, { headers });
  }

  public getCustomerVehicles(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${
      this.appSession.baseUrl + "/api/v1/parties"
    }/customers/${action}/vehicles`;
    return this.http.get(url, { headers });
  }

  getCustomerEmployeeList(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    container?: Map<string, string>
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;
    // if (filteringArgs && filteringArgs.length > 0) {

    filterQuery = GridUtils._buildFilteringExpression(filteringArgs, container);

    if (
      sortingArgs !== undefined &&
      sortingArgs !== null &&
      sortingArgs?.length > 0
    ) {
      sortingArgs.forEach((columnSorting, idx) => {
        if (idx < sortingArgs.length - 1) {
          orderQuery += GridUtils._buildSortExpression(columnSorting) + ";";
        } else {
          orderQuery += GridUtils._buildSortExpression(columnSorting);
        }
      });
    }
    if (filterQuery.length > 0) {
      headers = headers.append("query", filterQuery);
    }
    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }
    return this.http.get(
      this.buildDataUrl(
        paging,
        filteringArgs,
        sortingArgs,
        headers,
        "/employees/search?"
      ),
      { headers }
    );
    // }
  }

  // Grid filter helpers block

  private buildDataUrl(
    pagingArgs: any,
    filteringArgs: any,
    sortingArgs: any,
    header: HttpHeaders,
    path: string
  ): string {
    // let baseQueryString = `${DATA_URL}?$count=true`;
    let baseQueryString = `${
      this.appSession.baseUrl + "/api/v1/parties"
    }${path}`; // `${this.BASE_URL}/customers/search?`;
    let scrollingQuery = EMPTY_STRING;
    // let orderQuery = EMPTY_STRING;
    // let filterQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = GridUtils._buildScrollExpression(pagingArgs);
    }

    query +=
      scrollingQuery !== EMPTY_STRING ? `&${scrollingQuery}` : EMPTY_STRING;

    baseQueryString += query;

    return baseQueryString;
  }
}
